import { ForwardedRef, forwardRef, PropsWithoutRef, ReactElement, Ref } from "react";
import ReactSelect, { DropdownIndicatorProps, components as ReactSelectComponents, Props as ReactSelectProps, StylesConfig } from "react-select";
import { useTheme } from "styled-components";
import { mergeDeep } from "../../utils/mergeDeep.js";
import { ArrowDown02Linear } from "../Iconsax/ArrowDown02Linear.js";
import { ICONSAX_SIZE } from "../Iconsax/Iconsax.types.js";
import { ITheme } from "../Theme/Theme.types.js";
import { useTranslationsContext } from "../TranslationsProvider/TranslationsProvider.context.js";
import { selectStyles, StyledIconContainer, ValueContainerContent } from "./Select.style.js";
import { DefaultSelectProps, SelectOption, SelectProps, SelectValueContainerProps } from "./Select.types.js";

const defaultProps: DefaultSelectProps = {
    asAutocomplete: false,
    inError: false,
    variant: "glass",
};

const ValueContainer = <O extends SelectOption = SelectOption>({ children, ...props }: SelectValueContainerProps<O>) => {
    const { iconLeft } = props.selectProps;
    const IconComponent = iconLeft;

    return (
        <ReactSelectComponents.ValueContainer {...props}>
            <ValueContainerContent>
                {IconComponent && (
                    <StyledIconContainer>
                        <IconComponent size={ICONSAX_SIZE.SMALL} />
                    </StyledIconContainer>
                )}
                {children}
            </ValueContainerContent>
        </ReactSelectComponents.ValueContainer>
    );
};

const DropdownIndicator = <O extends SelectOption = SelectOption>(props: DropdownIndicatorProps<O>) => {
    return (
        <ReactSelectComponents.DropdownIndicator {...props}>
            <ArrowDown02Linear size={ICONSAX_SIZE.SMALL} />
        </ReactSelectComponents.DropdownIndicator>
    );
};

/**
 * @deprecated This component should not be used directly.
 * Use {@link SelectV2} instead which provides the complete select functionality.
 * This component will be removed in a future version.
 */
export const Select = forwardRef(<O extends SelectOption>(props: SelectProps<O>, ref: Ref<any>) => {
    const { options, onChange, value, disabled, size, ...newProps } = props;

    const theme = useTheme() as ITheme;

    const { translations } = useTranslationsContext();
    const { styles, asAutocomplete, iconLeft, components, variant, inError, noOptionsMessage, placeholder, ...attributes } = {
        ...defaultProps,
        ...newProps,
    };

    const handleChange: ReactSelectProps<O, false>["onChange"] = (val): void => {
        if (!onChange) return;
        onChange(val?.value);
    };

    const optionValue = value !== undefined ? options.find((v) => v.value === value) : null;

    const mergedStyles = mergeDeep(selectStyles(theme, asAutocomplete, variant, inError, size), styles !== undefined ? styles : {}) as StylesConfig<
        O,
        false
    >;

    return (
        <ReactSelect<O, false>
            {...attributes}
            ref={ref}
            isMulti={false}
            isDisabled={disabled}
            value={optionValue}
            onChange={handleChange}
            noOptionsMessage={noOptionsMessage || translations.select?.noOptions ? () => translations.select?.noOptions : undefined}
            styles={mergedStyles}
            options={options}
            iconLeft={iconLeft}
            placeholder={placeholder || translations.select?.placeholder}
            components={{
                DropdownIndicator,
                ...components,
                ...(iconLeft ? { ValueContainer } : {}),
            }}
        />
    );
    // eslint-disable-next-line no-use-before-define
}) as <T extends SelectOption>(p: PropsWithoutRef<SelectProps<T>> & { ref?: ForwardedRef<HTMLInputElement> }) => ReactElement;
